import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { getComparatorByExtractor, Order } from '../utils';
import { Customer, Region } from '../types';
import RolePermissionsToggle from './RolePermissionsToggle';
import { useAuth0 } from '@auth0/auth0-react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import CircularProgress from '@mui/material/CircularProgress';

const CustomerConfiguration: React.FC = () => {
  const { user: auth0User } = useAuth0();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<'name' | 'regionName'>('name');
  const [showInactive, setShowInactive] = useState(false);
  const [editingAccountNumber, setEditingAccountNumber] = useState<Record<string, boolean>>({});
  const [savingAccountId, setSavingAccountId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim().toLowerCase());
      setSearching(false);
    }, 300);

    return () => clearTimeout(handle);
  }, [searchTerm]);

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        const [regionsResponse, customersResponse] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL}/regions`),
          axios.get(`${process.env.REACT_APP_API_URL}/customers`),
        ]);
        const fetchedRegions = regionsResponse.data as Region[];
        const fetchedCustomers = customersResponse.data as Customer[];

        setRegions(fetchedRegions);
        setCustomers(fetchedCustomers);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const handleAccountNumberEditClick = (customerId: string) => {
    setEditingAccountNumber((prev) => ({
      ...prev,
      [customerId]: true,
    }));
  };

  const saveAccountNumber = async (customerId: string, accountNumber: string) => {
    setSavingAccountId(customerId);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/update-customer`, {
        customer_id: customerId,
        account_number: accountNumber,
        current_auth0_sub: auth0User?.sub,
      });

      setCustomers((prev) =>
        prev.map((c) =>
          c.id === customerId ? { ...c, account_number: accountNumber } : c
        )
      );
    } catch (error) {
      console.error('Error updating customer account number:', error);
    } finally {
      setSavingAccountId(null);
      setEditingAccountNumber((prev) => ({
        ...prev,
        [customerId]: false,
      }));
    }
  };

  const handleRegionChange = async (
    event: SelectChangeEvent<string>,
    customerId: string
  ) => {
    const newRegionId = event.target.value;
    const selectedRegion = regions.find((region) => region.id === newRegionId) || null;

    setCustomers((prevCustomers) =>
      prevCustomers.map((customer) =>
        customer.id === customerId
          ? { ...customer, region_id: newRegionId, region: selectedRegion }
          : customer
      )
    );

    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/update-customer`, {
        customer_id: customerId,
        region_id: newRegionId,
        current_auth0_sub: auth0User?.sub,
      });
    } catch (error) {
      console.error('Error updating customer region:', error);
    }
  };

  const handleRequestSort = (property: 'name' | 'regionName') => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedCustomers = useMemo(() => {
    return [...customers].sort(
      getComparatorByExtractor<Customer>(
        order,
        orderBy === 'name'
          ? (customer) => customer.name
          : (customer) => customer.region?.name || ''
      )
    );
  }, [customers, order, orderBy]);

  const filteredCustomers = useMemo(() => {
    const term = debouncedSearch;

    return sortedCustomers.filter((customer) => {
      const isActiveMatch = showInactive ? customer.active === false : customer.active === true;
      if (!isActiveMatch) return false;
      if (!term) return true;

      const name = (customer.name || '').toLowerCase();
      const accountNumber = (customer.account_number || '').toLowerCase();
      const regionName = (customer.region?.name || '').toLowerCase();

      return (
        name.includes(term) ||
        accountNumber.includes(term) ||
        regionName.includes(term)
      );
    });
  }, [sortedCustomers, showInactive, debouncedSearch]);

  const renderedRows = useMemo(
    () =>
      filteredCustomers.map((customer) => (
        <TableRow key={customer.id}>
          <TableCell sx={{ padding: '4px 8px' }}>
            {customer.name}
          </TableCell>
          <TableCell sx={{ padding: '4px 8px', width: '12rem' }}>
            <TextField
              fullWidth
              variant="standard"
              defaultValue={customer.account_number ?? ''}
              onBlur={(e) => {
                if (!editingAccountNumber[customer.id]) return;

                const value = e.target.value;
                void saveAccountNumber(customer.id, value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const input = e.currentTarget.querySelector('input');
                  input?.blur();
                }
              }}
              disabled={
                savingAccountId === customer.id ||
                !editingAccountNumber[customer.id]
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleAccountNumberEditClick(customer.id)}
                      disabled={savingAccountId === customer.id}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              inputProps={{
                maxLength: 50,
                style: { fontSize: '0.9rem' },
              }}
            />
          </TableCell>
          <TableCell sx={{ padding: '4px 8px' }}>
            <FormControl variant="standard" fullWidth>
              <Select
                value={customer.region_id || ''}
                onChange={(event) => handleRegionChange(event, customer.id)}
                displayEmpty
                sx={{
                  fontSize: '0.9rem',
                  width: '10rem',
                }}
              >
                {regions.map((region) => (
                  <MenuItem key={region.id} value={region.id}>
                    {region.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </TableCell>
        </TableRow>
      )),
    [filteredCustomers, savingAccountId, editingAccountNumber, regions, saveAccountNumber, handleRegionChange]
  );
  return (
    <Container
      maxWidth="lg"
      sx={{
        padding: 2,
        margin: '0 auto',
      }}
    >
      <Typography variant="h4" gutterBottom align="center">
        Customer Configuration
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
            }
            label={showInactive ? 'Inactive Accounts' : 'Active Accounts'}
          />
        </Box>
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          marginTop: 2,
        }}
      >
        <Box
          sx={{
            maxWidth: 350,
            flexShrink: 0,
            border: 'none',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <RolePermissionsToggle roleId={3} label="Member Access" />
            <RolePermissionsToggle roleId={5} label="Advanced Access" />
          </Box>
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            maxWidth: 800,
          }}
        >
          {/* Search bar */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mb: 1,
            }}
          >
          <TextField
            size="small"
            label="Search"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setSearching(true);
            }}
            placeholder="Search name, account, or region..."
            sx={{ mb: 1, maxWidth: 300 }}
          />
          </Box>

          {loading || searching ? (
            <Box
              sx={{
                height: 300,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid #e0e0e0',
                borderRadius: '0px 0px 4px 4px',
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: '400px',
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: '0px 0px 4px 4px',
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sortDirection={orderBy === 'name' ? order : false}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Customer Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="left">Account Number</TableCell>
                    <TableCell
                      align="center"
                      sortDirection={orderBy === 'regionName' ? order : false}
                    >
                      <TableSortLabel
                        active={orderBy === 'regionName'}
                        direction={orderBy === 'regionName' ? order : 'asc'}
                        onClick={() => handleRequestSort('regionName')}
                      >
                        Region
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderedRows}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Container>
  );
};

export default CustomerConfiguration;
